import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Clock, PenLine, BookCheck, Pencil, BarChart3, ArrowLeft } from "lucide-react";
import { subjectTheme } from "@/lib/subjects";
import { dueGroup, fmtDue, DUE_GROUPS } from "@/lib/due";

const STATUS = {
  draft: { label: "Draft", cls: "bg-[#F3F4F6] text-[#6B7280]" },
  ready_for_review: { label: "Draft", cls: "bg-[#FFFBEB] text-[#B45309]" },
  approved: { label: "Approved", cls: "bg-[#EFF6FF] text-[#2563EB]" },
  released: { label: "Live", cls: "bg-[#ECFDF5] text-[#059669]" },
};
const isWritingHw = (hw) => hw.format === "writing" || hw.questions?.[0]?.type === "writing";

function SubjectBox({ name, section, items, onOpen }) {
  const t = subjectTheme(section, name);
  const live = items.filter((h) => h.status === "released").length;
  return (
    <button type="button" onClick={onOpen} data-testid={`hw-subject-${section}`}
      className="flex min-h-[130px] flex-col justify-between rounded-2xl p-5 text-left text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg" style={{ backgroundColor: t.hex }}>
      <div className="text-lg font-extrabold leading-tight">{name}</div>
      <div className="mt-4 flex justify-between text-xs font-semibold text-white/90">
        <span>{items.length} homework</span><span>{live} live · {items.length - live} draft</span>
      </div>
    </button>
  );
}

function HwRow({ hw }) {
  const st = STATUS[hw.status] || STATUS.draft;
  const w = isWritingHw(hw);
  const link = "inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-[#4B2BB8] hover:bg-[#F3F0FF]";
  return (
    <div className="ember-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`hw-row-${hw.id}`}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${w ? "bg-[#FFF1F2] text-[#E11D48]" : "bg-[#EDE9FE] text-[#5B21B6]"}`}>{w ? <PenLine className="h-5 w-5" /> : <FileText className="h-5 w-5" />}</span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-heading font-bold text-[#1E1B2E]">{hw.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-[#948EAB]">
          <span>{hw.format === "mixed" ? "MCQ + written" : hw.format === "saq" ? "Short answer" : w ? "Writing task" : `${hw.question_count} MCQs`}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {hw.mode === "timed" ? `${hw.time_limit}m` : "Practice"}</span>
          {hw.due_date && <span className="font-semibold text-[#4B2BB8]" data-testid={`hw-due-${hw.id}`}>{fmtDue(hw.due_date)}</span>}
          <span>{(hw.assigned_to || []).length} student{(hw.assigned_to || []).length === 1 ? "" : "s"}</span>
          {hw.created_at && <span data-testid={`hw-date-${hw.id}`}>Uploaded {new Date(hw.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>}
        </div>
      </div>
      <span className={`w-fit rounded-full px-3 py-1 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
      <div className="flex flex-wrap items-center gap-1">
        {hw.file_url && <a href={`${BACKEND_URL}${hw.file_url}`} target="_blank" rel="noreferrer" className={link} data-testid={`hw-pdf-${hw.id}`}><FileText className="h-3.5 w-3.5" /> Exam PDF</a>}
        {hw.solution_url && <a href={`${BACKEND_URL}${hw.solution_url}`} target="_blank" rel="noreferrer" className={link} data-testid={`hw-solutions-${hw.id}`}><BookCheck className="h-3.5 w-3.5" /> Solutions</a>}
        <Link to={`/tutor/homework/${hw.id}/review`} className={link} data-testid={`hw-edit-${hw.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Link>
        <Link to={w ? "/tutor/writing" : "/tutor/results"} className={link} data-testid={`hw-results-${hw.id}`}><BarChart3 className="h-3.5 w-3.5" /> Results</Link>
      </div>
    </div>
  );
}

function SubjectDetail({ name, items, onBack }) {
  const weeks = useMemo(() => {
    const g = {};
    items.forEach((h) => { const k = dueGroup(h.due_date); (g[k] = g[k] || []).push(h); });
    Object.values(g).forEach((l) => l.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "") || (b.created_at || "").localeCompare(a.created_at || "")));
    return DUE_GROUPS.filter(([k]) => g[k]).map(([k, label]) => [k, g[k], label]);
  }, [items]);
  return (
    <div data-testid="hw-subject-detail">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#4B2BB8] hover:underline" data-testid="hw-subject-back"><ArrowLeft className="h-4 w-4" /> All subjects</button>
      <h2 className="mb-4 font-heading text-xl font-bold text-[#1E1B2E]">{name}</h2>
      {items.length === 0 && <div className="ember-card p-8 text-center text-sm text-[#948EAB]">No homework in this subject yet.</div>}
      <div className="space-y-6">
        {weeks.map(([wk, list, label]) => (
          <div key={wk} data-testid={`hw-group-${wk}`}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-[#948EAB]">{label}</div>
            <div className="space-y-2">{list.map((h) => <HwRow key={h.id} hw={h} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomeworkList() {
  const [rows, setRows] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [program, setProgram] = useState(() => localStorage.getItem("ec_hw_prog") || "jmss");
  const [section, setSection] = useState(null);
  useEffect(() => { api.get("/homework").then((r) => setRows(r.data)); api.get("/programs").then((r) => setPrograms(r.data)); }, []);
  useEffect(() => { localStorage.setItem("ec_hw_prog", program); setSection(null); }, [program]);
  if (!rows || !programs) return <Loading />;
  const sections = programs[program]?.sections || [];
  const inProg = rows.filter((h) => h.program === program);
  const sec = sections.find((s) => s.key === section);

  return (
    <div className="animate-fade-up" data-testid="tutor-homework-page">
      <PageHeader title="Homework" subtitle="Every homework and mock exam, by program and subject."
        right={<Link to="/tutor/upload"><Button className="h-11 rounded-xl ember-gradient font-bold"><Upload className="mr-2 h-4 w-4" /> Upload Homework</Button></Link>} />
      <div className="mb-6 flex flex-wrap gap-1 rounded-xl bg-[#F5F3FF] p-1 sm:inline-flex" data-testid="hw-program-tabs">
        {Object.values(programs).map((p) => (
          <button key={p.key} onClick={() => setProgram(p.key)} data-testid={`hw-program-${p.key}`}
            className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${program === p.key ? "bg-white text-[#4B2BB8] shadow-sm" : "text-[#6B6488] hover:text-[#4B2BB8]"}`}>
            {p.name} <span className="ml-1 text-xs opacity-70">{rows.filter((h) => h.program === p.key).length}</span>
          </button>
        ))}
      </div>
      {sec ? (
        <SubjectDetail name={sec.name} items={inProg.filter((h) => h.section === sec.key)} onBack={() => setSection(null)} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="hw-subject-grid">
          {sections.map((s) => <SubjectBox key={s.key} name={s.name} section={s.key} items={inProg.filter((h) => h.section === s.key)} onOpen={() => setSection(s.key)} />)}
        </div>
      )}
    </div>
  );
}
